import React, { useCallback, useEffect, useRef, useState, ReactNode } from 'react'
import { Dialog, DialogContent, DialogTitle, Divider } from '@mui/material'
import { ThemeProvider } from '@mui/material_modern'
import { createTheme } from '@mui/material/styles'

import { useGetOrgImageQuery } from 'gen/rtk-openapi'
import { getImageUrl } from 'services/assets'

import PrintDialogTitle from './components/dialog-header/PrintDialogTitle'
import { CustomSizePaper } from './components/dialog-header/generic'
import Loading from 'components/generic/Loading'

import { lightPalette } from 'themes/application'
import { CHURN_ZERO_EVENTS, PRINT_DIALOG_TYPES } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
interface PrintDialogProps {
  open: boolean
  title?: string
  children: ReactNode
  onClose: () => void
  dialogId?: string
  shouldPrintTitle?: boolean
  headerOverride?: ReactNode
  showLogoAddress?: boolean
  dialogType?: PrintDialogType
  onAfterPrint?: () => void
}

interface PrintDialogType {
  key: string
  layoutWidth: string
  maxWidth: number
  logoHeight?: string
  fontSize?: string
  lineHeight?: string
  paperSize?: string
  paperMargin: string
}

interface CustomError {
  data?: {
    error?: string
  }
}

const PrintDialog: React.FC<PrintDialogProps> = ({
  open,
  title = '',
  children,
  onClose,
  dialogId = 'printable-purchase-ticket',
  shouldPrintTitle = true,
  headerOverride = null,
  showLogoAddress = false,
  dialogType = PRINT_DIALOG_TYPES.COMMON,
  onAfterPrint = () => {},
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { logo } = useAppSelector((st: any) => st.settings.details)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { organization } = useAppSelector((st: any) => st.auth)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: logoImage } = useGetOrgImageQuery({ filename: logo }, { skip: !logo })
  const dialogEl = useRef<HTMLDivElement | null>(null)
  const refPrintButton = useRef<HTMLButtonElement | null>(null)
  const [loading, setLoading] = useState(showLogoAddress && !!logo) // No need to wait for logo if there is no logo
  const [logoUrl, setLogoUrl] = useState(null)

  const eventName = CHURN_ZERO_EVENTS.GET_S3_IMAGE

  const handleS3Error = useCallback(
    error => {
      const errorMsg = error?.data?.error || 'There was an error getting the image'
      trackEvent({ eventName, errorMsg })
      setLoading(false)
    },
    [eventName],
  )

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const fetchLogoUrl = async () => {
      if (logo) {
        try {
          const url = await getImageUrl(logo, logoImage, { signal })
          setLogoUrl(url)
        } catch (error) {
          handleS3Error(error as CustomError)
        }
      }
    }
    fetchLogoUrl()

    return () => {
      controller.abort()
    }
  }, [logo, handleS3Error, logoImage])

  useEffect(() => {
    if (!logoUrl && loading) {
      const timeoutId = setTimeout(() => {
        setLoading(false) // Hide loading modal after timeout
      }, 10000)
      return () => clearTimeout(timeoutId)
    }
  }, [logoUrl])

  const themeLight = createTheme({ palette: lightPalette })

  const renderDialogContent = () => {
    if (dialogType.key === PRINT_DIALOG_TYPES.COMMON.key) {
      return (
        <ThemeProvider theme={themeLight}>
          {headerOverride && <div className="printOnly">{headerOverride}</div>}
          <table width="100%">
            <thead className="page-header">
              <tr>
                <th>
                  <DialogTitle id={dialogId} sx={{ p: 0, pt: 2 }}>
                    <PrintDialogTitle
                      refDialog={dialogEl}
                      refPrintButton={refPrintButton}
                      onCloseDialog={onClose}
                      title={title}
                      dialogType={dialogType}
                      shouldPrintTitle={shouldPrintTitle}
                      showLogoAddress={showLogoAddress}
                      logoUrl={logoUrl}
                      onAfterLoadLogo={() => setLoading(false)}
                      onAfterPrint={onAfterPrint}
                    />
                    <Divider />
                  </DialogTitle>
                </th>
              </tr>
            </thead>
            <tbody className="page-content">
              <DialogContent sx={{ p: 2 }}>{children}</DialogContent>
            </tbody>
            <tfoot className="page-footer" style={{ height: '10px' }} />
          </table>
        </ThemeProvider>
      )
    }
    return (
      <ThemeProvider theme={themeLight}>
        {headerOverride && <div className="printOnly">{headerOverride}</div>}
        <DialogTitle id={dialogId} sx={{ p: 0 }}>
          <PrintDialogTitle
            refDialog={dialogEl}
            refPrintButton={refPrintButton}
            onCloseDialog={onClose}
            title={title}
            dialogType={dialogType}
            shouldPrintTitle={shouldPrintTitle}
            showLogoAddress={showLogoAddress}
            logoUrl={logoUrl}
            onAfterLoadLogo={() => setLoading(false)}
            onAfterPrint={onAfterPrint}
          />
        </DialogTitle>
        <DialogContent className="page-content" sx={{ padding: 2 }}>
          {children}
        </DialogContent>
      </ThemeProvider>
    )
  }

  useEffect(() => {
    let newTimeout: NodeJS.Timeout | undefined
    // don't bother launching the print dialog for the test org. This way we can test/inspect the print preview modal
    const isTestOrg = organization === 'test-org'
    if (open && !isTestOrg && !loading) {
      newTimeout = setTimeout(() => {
        if (refPrintButton?.current) {
          refPrintButton.current.click()
        }
      }, 300)
    }
    // Return a cleanup function from the useEffect hook
    return () => {
      if (newTimeout) {
        clearTimeout(newTimeout)
      }
    }
  }, [open, organization, loading])

  return (
    <>
      {open && loading && <Loading />}
      <ThemeProvider theme={themeLight}>
        <Dialog
          sx={{ '& .MuiDialog-paper': { width: dialogType.layoutWidth, maxWidth: dialogType.maxWidth } }}
          aria-labelledby="customized-dialog-title"
          open={open}
          id={dialogId}
          onClose={onClose}
        >
          <CustomSizePaper ref={dialogEl}>
            {dialogType.paperSize && (
              <style>{`@page { size: ${dialogType.paperSize}; margin: ${dialogType.paperMargin}; }`}</style>
            )}
            {renderDialogContent()}
          </CustomSizePaper>
        </Dialog>
      </ThemeProvider>
    </>
  )
}

export default PrintDialog
